import React, { useEffect, useState } from 'react';
import apiClient from '../api/client';

const DEFAULT_RULE = {
  campaign_id: '',
  if_json: '{\n  "metric": "article.cpo",\n  "operator": "<=",\n  "value": 100\n}',
  action_json: '{\n  "type": "adjust_bid",\n  "payload": {\n    "multiplier": 1.1\n  }\n}',
};

const METRIC_HINTS = [
  {
    title: 'Общие метрики кампании (stats)',
    keys: ['stats.cost', 'stats.revenue', 'stats.orders', 'stats.ctr', 'stats.cpc', 'stats.cpo', 'stats.drr'],
  },
  {
    title: 'Артикул / ключевая фраза (article)',
    keys: ['article.keyword', 'article.nm_id', 'article.current_bid', 'article.spend', 'article.revenue', 'article.cpo', 'article.drr'],
  },
];

export default function RulesEditor() {
  const [ruleType, setRuleType] = useState('bidder');
  const [campaignId, setCampaignId] = useState('');
  const [rules, setRules] = useState([]);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(DEFAULT_RULE);

  useEffect(() => {
    loadRules();
  }, [ruleType]);

  const loadRules = async () => {
    setStatus('Загрузка правил...');
    const endpoint = ruleType === 'bidder' ? '/bidder/rules' : '/negative-phrases/rules';
    try {
      const { data } = await apiClient.get(endpoint);
      setRules(data.rules || []);
      setStatus('');
    } catch (error) {
      console.error('Ошибка загрузки правил:', error);
      setStatus('Не удалось загрузить правила');
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      campaign_id: Number(form.campaign_id),
      if_json: JSON.parse(form.if_json),
      action_json: JSON.parse(form.action_json),
    };
    setLoading(true);
    try {
      const endpoint = ruleType === 'bidder' ? '/bidder/rules' : '/negative-phrases/rules';
      await apiClient.post(endpoint, payload);
      setStatus('Правило создано');
      setForm(DEFAULT_RULE);
      loadRules();
    } catch (error) {
      console.error('Ошибка создания правила:', error);
      setStatus(error.response?.data?.message || 'Ошибка создания правила');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const endpoint = ruleType === 'bidder' ? '/bidder/rules' : '/negative-phrases/rules';
      await apiClient.delete(`${endpoint}/${id}`);
      setStatus('Правило удалено');
      loadRules();
    } catch (error) {
      console.error('Ошибка удаления:', error);
      setStatus('Не удалось удалить правило');
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      <h2 className="text-2xl font-bold">Редактор правил</h2>
      <div className="flex gap-3">
        {['bidder', 'negative'].map((type) => (
          <button
            key={type}
            onClick={() => setRuleType(type)}
            className={`px-4 py-2 rounded ${ruleType === type ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-700'}`}
          >
            {type === 'bidder' ? 'Правила биддера' : 'Минус-фразы'}
          </button>
        ))}
      </div>

      <form onSubmit={handleCreate} className="bg-white border rounded p-4 space-y-3">
        <label className="text-sm font-semibold block">campaign_id</label>
        <input
          type="number"
          value={form.campaign_id}
          onChange={(e) => setForm({ ...form, campaign_id: e.target.value })}
          className="w-full border rounded px-3 py-2"
          required
        />
        <label className="text-sm font-semibold block">if_json</label>
        <textarea
          rows={6}
          value={form.if_json}
          onChange={(e) => setForm({ ...form, if_json: e.target.value })}
          className="w-full border rounded px-3 py-2 font-mono text-xs"
        />
        <label className="text-sm font-semibold block">action_json</label>
        <textarea
          rows={6}
          value={form.action_json}
          onChange={(e) => setForm({ ...form, action_json: e.target.value })}
          className="w-full border rounded px-3 py-2 font-mono text-xs"
        />
        <button
          type="submit"
          disabled={loading}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-500"
        >
          {loading ? 'Сохраняем...' : 'Сохранить правило'}
        </button>
      </form>

      <div className="bg-gray-50 border rounded p-4 text-sm text-gray-600 space-y-2">
        <p className="font-semibold text-gray-700">Подсказка по контексту</p>
        {METRIC_HINTS.map((section) => (
          <div key={section.title}>
            <div className="text-xs font-semibold uppercase text-gray-500">{section.title}</div>
            <div className="flex flex-wrap gap-2 mt-1">
              {section.keys.map((key) => (
                <span key={key} className="px-2 py-1 text-xs rounded bg-white border">
                  {key}
                </span>
              ))}
            </div>
          </div>
        ))}
        <p className="text-xs text-gray-500">
          В блоке `if_json` можно использовать `stats` и `article`, например:
        </p>
        <pre className="bg-white p-2 rounded text-xs text-gray-800 font-mono">
{'{\n  "metric": "article.cpo",\n  "operator": "<=",\n  "value": 120\n}'}
        </pre>
      </div>

      {status && <p className="text-sm text-gray-600">{status}</p>}

      <div className="bg-white border rounded p-4">
        <h3 className="font-semibold mb-3">Существующие правила ({rules.length})</h3>
        <ul className="divide-y">
          {rules.map((rule) => (
            <li key={rule.id} className="py-3 flex justify-between items-start gap-4">
              <div>
                <p className="text-sm font-semibold">campaign {rule.campaign_id} (#{rule.id})</p>
                <p className="text-xs text-gray-500">if: {rule.if_json}</p>
                <p className="text-xs text-gray-500">action: {rule.action_json}</p>
              </div>
              <button
                onClick={() => handleDelete(rule.id)}
                className="text-red-600 text-sm"
              >
                Удалить
              </button>
            </li>
          ))}
          {!rules.length && <li className="py-2 text-sm text-gray-500">Нет правил</li>}
        </ul>
      </div>
    </div>
  );
}
